/**
 * penelouxEnthalpy.js
 * 
 * Peng-Robinson (PR78) Equation of State with Peneloux (1982) volume shift correction.
 * Includes Søreid-Whitson (1992) and Knapp et al. (1982) Binary Interaction Parameters (BIPs).
 * 
 * Used for accurate residual enthalpy (H_res) calculations, specifically for JT valves
 * and other isenthalpic/isobaric processes in natural gas processing.
 */

/**
 * Calculates the residual enthalpy H_res = H(T,P) - H_ig(T) in J/mol.
 * 
 * @param {number} T_K - Temperature in Kelvin
 * @param {number} P_bar - Pressure in bar
 * @param {Float64Array|number[]} z - Mole fractions (length 10)
 * @param {Array} COMP_LIST - Component properties (Tc, Pc, omega, MW)
 * @param {number} R_BAR_L_MOL_K - Gas constant 0.08314 bar·L/(mol·K)
 * @param {string} phaseHint - 'V' for vapour, 'L' for liquid
 * @returns {number} H_res in J/mol
 */
export function mixtureEnthalpyDeparture_Jmol(T_K, P_bar, z, COMP_LIST, R_BAR_L_MOL_K, phaseHint = 'V') {
  const n = z.length;
  const R = R_BAR_L_MOL_K;
  const RT = R * T_K;

  // 1. Per-component PR78 parameters and Peneloux volume shifts
  const a = new Float64Array(n);
  const b = new Float64Array(n);
  const da_dT = new Float64Array(n);
  const c = new Float64Array(n);

  for (let i = 0; i < n; i++) {
    const comp = COMP_LIST[i];
    const Tr = T_K / comp.Tc;
    
    // kappa calculation (PR78)
    let kappa;
    if (comp.omega <= 0.491) {
      kappa = 0.37464 + 1.54226 * comp.omega - 0.26992 * comp.omega * comp.omega;
    } else {
      kappa = 0.379642 + 1.48503 * comp.omega - 0.164423 * comp.omega * comp.omega + 0.016666 * Math.pow(comp.omega, 3);
    }

    const alpha = Math.pow(1 + kappa * (1 - Math.sqrt(Tr)), 2);
    
    // a_i(T) and b_i
    a[i] = 0.45724 * R * R * comp.Tc * comp.Tc / comp.Pc * alpha;
    b[i] = 0.07780 * R * comp.Tc / comp.Pc;
    
    // da_i/dT
    da_dT[i] = -0.45724 * R * R * comp.Tc * comp.Tc / comp.Pc * kappa * Math.sqrt(alpha / comp.Tc) / Math.sqrt(T_K);

    // Peneloux volume shift
    const Zc_i = 0.2905 - 0.085 * comp.omega;
    c[i] = (0.40768 * R * comp.Tc / comp.Pc) * (0.29441 - Zc_i);
  }

  // 2. BIPs and Mixing Rules
  // BIP Table (kij) based on STATE.md
  // Order: [CH4, C2, C3, nC4, nC5, nC6, N2, CO2, H2S, H2O]
  const kij = (i, j, T) => {
    if (i === j) return 0;
    const [idx1, idx2] = i < j ? [i, j] : [j, i];
    
    // Key values from STATE.md
    if (idx1 === 0) { // CH4
      if (idx2 === 6) return 0.0311; // N2
      if (idx2 === 7) return 0.0919; // CO2
      if (idx2 === 8) return 0.0714; // H2S
      if (idx2 === 2) return 0.007;  // C3
      if (idx2 === 3) return 0.013;  // C4
      if (idx2 === 4) return 0.020;  // C5
      if (idx2 === 5) return 0.028;  // C6
    }
    if (idx1 === 6) { // N2
      if (idx2 === 1) return 0.0515; // C2
      if (idx2 === 2) return 0.0852; // C3
      if (idx2 >= 3 && idx2 <= 5) return 0.100; // C4+
      if (idx2 === 8) return 0.1696; // H2S
    }
    if (idx1 === 7) { // CO2
      if (idx2 === 1) return 0.1322; // C2
      if (idx2 === 2) return 0.1241; // C3
      if (idx2 >= 3 && idx2 <= 5) return 0.120; // C4+
      if (idx2 === 8) return 0.0974; // H2S
    }
    if (idx1 === 8) { // H2S
      if (idx2 === 1) return 0.0837; // C2
      if (idx2 === 2) return 0.0878; // C3
      if (idx2 === 3) return 0.060;  // C4
      if (idx2 === 4) return 0.060;  // C5
      if (idx2 === 5) return 0.050;  // C6
    }
    if (idx1 === 9) { // H2O
      if (idx2 === 7) return 0.1896; // CO2
      if (idx2 === 6) return 0.4778; // N2
      if (idx2 === 0) return 0.485;  // CH4
      if (idx2 === 1) return 0.492;  // C2
      if (idx2 >= 2 && idx2 <= 5) return 0.5525; // C3+
      if (idx2 === 8) return 0.19031 - 0.05965 * (T / 373.2); // H2S (T-dependent)
    }
    
    return 0; // Default
  };

  let a_mix = 0;
  let b_mix = 0;
  let da_mix_dT = 0;
  let c_mix = 0;

  for (let i = 0; i < n; i++) {
    b_mix += z[i] * b[i];
    c_mix += z[i] * c[i];
    for (let j = 0; j < n; j++) {
      const k_val = kij(i, j, T_K);
      const aij = Math.sqrt(a[i] * a[j]) * (1 - k_val);
      a_mix += z[i] * z[j] * aij;
      
      // da_mix/dT term
      // Note: H2O/H2S kij is T-dependent, dkij/dT = -0.05965 / 373.2
      let dkij_dT = 0;
      if ((i === 8 && j === 9) || (i === 9 && j === 8)) {
        dkij_dT = -0.05965 / 373.2;
      }
      
      const aij_val = Math.sqrt(Math.max(a[i] * a[j], 0));
      const term1 = aij_val > 1e-30
        ? (da_dT[i] * a[j] + a[i] * da_dT[j]) / (2 * aij_val)
        : 0;
      const daij_dT = term1 * (1 - k_val) - aij_val * dkij_dT;
      
      da_mix_dT += z[i] * z[j] * daij_dT;
    }
  }

  // 3. Solve PR cubic for Z (Vapour root)
  const A = a_mix * P_bar / (RT * RT);
  const B = b_mix * P_bar / RT;

  // Z³ - (1-B)Z² + (A-3B²-2B)Z - (AB-B²-B³) = 0
  const c2 = -(1 - B);
  const c1 = A - 3 * B * B - 2 * B;
  const c0 = -(A * B - B * B - B * B * B);

  const roots = solveCubic(c2, c1, c0).filter(r => r > B + 1e-10);
  if (roots.length === 0) return 0;
  
  // Select root based on phase hint
  const Z = phaseHint === 'L' ? Math.min(...roots) : Math.max(...roots);
  if (!Z || isNaN(Z) || !isFinite(Z)) return 0;

  // 4. PR Enthalpy Departure (bar·L/mol)
  const sqrt2 = Math.sqrt(2);
  const ln_num = Z + (1 + sqrt2) * B;
  const ln_den = Z + (1 - sqrt2) * B;
  // Guard: denominator must be positive; if Z is too small (deep liquid), clamp
  if (ln_den <= 1e-12 || ln_num <= 1e-12) return 0;
  const lnTerm = Math.log(ln_num / ln_den);
  if (!isFinite(lnTerm)) return 0;
  
  // H_dep_PR = RT(Z-1) + (T * da_mix/dT - a_mix) / (2√2 * b_mix) * lnTerm
  const H_dep_PR = RT * (Z - 1) + (T_K * da_mix_dT - a_mix) / (2 * sqrt2 * b_mix) * lnTerm;

  // 5. Peneloux correction (bar·L/mol)
  const H_dep_corr = H_dep_PR - P_bar * c_mix;

  const result = H_dep_corr * 100;
  return isFinite(result) ? result : 0;
}

/**
 * Analytical solver for cubic equation: x³ + c2x² + c1x + c0 = 0
 */
function solveCubic(c2, c1, c0) {
  const Q = (3 * c1 - c2 * c2) / 9;
  const R = (9 * c2 * c1 - 27 * c0 - 2 * Math.pow(c2, 3)) / 54;
  const D = Math.pow(Q, 3) + Math.pow(R, 2);

  if (D > 0) {
    // One real root
    const S = Math.cbrt(R + Math.sqrt(D));
    const T = Math.cbrt(R - Math.sqrt(D));
    return [S + T - c2 / 3];
  } else if (D === 0) {
    // Three real roots, at least two are equal
    const S = Math.cbrt(R);
    return [2 * S - c2 / 3, -S - c2 / 3];
  } else {
    // Three distinct real roots
    const theta = Math.acos(R / Math.sqrt(Math.pow(-Q, 3)));
    const sqrtQ = Math.sqrt(-Q);
    return [
      2 * sqrtQ * Math.cos(theta / 3) - c2 / 3,
      2 * sqrtQ * Math.cos((theta + 2 * Math.PI) / 3) - c2 / 3,
      2 * sqrtQ * Math.cos((theta + 4 * Math.PI) / 3) - c2 / 3
    ];
  }
}
